import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';

const AuthCallback = () => {
  const { handleCallback } = useAuth();
  const [status, setStatus] = useState<string>('processing');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processCallback = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const oauthError = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        if (oauthError) {
          throw new Error(errorDescription || `OAuth error: ${oauthError}`);
        }

        if (!code || !state) {
          throw new Error('Missing required OAuth parameters');
        }

        await handleCallback(code, state);
        
        setStatus('success');
        
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);

      } catch (err: any) {
        if (import.meta.env.DEV) {
          console.error('OAuth callback error:', err);
        }
        setError(err.message);
        setStatus('error');
      }
    };

    processCallback();
  }, [handleCallback]);

  if (status === 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <h2 className="mt-4 text-lg font-medium text-gray-900">
              Completing Authentication...
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Please wait while we complete your sign-in process.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="mt-4 text-lg font-medium text-gray-900">
              Authentication Successful!
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              You have been successfully signed in. Redirecting you to the application...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="mt-4 text-lg font-medium text-gray-900">
              Authentication Failed
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {error || 'An error occurred during authentication.'}
            </p>
            <button
              onClick={() => window.location.href = '/'}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AuthCallback;
